import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Briefcase, Lock, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords don't match"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    }
    setLoading(false);
  };

  if (!token) return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
      <div className="text-center"><p className="text-[#1C2A24]/60">Invalid reset link.</p><Link to="/auth"><Button className="rounded-full bg-[#047857] text-white mt-4">Go to Login</Button></Link></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl border border-[#E5E0D8] shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={18} className="text-white" strokeWidth={1.5} /></div>
            <h1 className="text-2xl font-medium tracking-tight" style={{fontFamily:'Outfit'}}>New Password</h1>
          </div>
          {done ? (
            <div className="text-center py-6" data-testid="reset-done">
              <CheckCircle2 size={48} className="text-[#047857] mx-auto mb-4" strokeWidth={1.5} />
              <h2 className="text-lg font-medium mb-2" style={{fontFamily:'Outfit'}}>Password Updated!</h2>
              <p className="text-[#1C2A24]/60 text-sm mb-6">Your password has been reset. You can now log in.</p>
              <Link to="/auth"><Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white" data-testid="login-after-reset">Go to Login</Button></Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-sm text-[#1C2A24]/70">New Password</Label>
                <div className="relative mt-1">
                  <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1C2A24]/40" strokeWidth={1.5} />
                  <Input data-testid="new-password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="pl-10 rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] h-12" placeholder="New password" required />
                </div>
              </div>
              <div>
                <Label className="text-sm text-[#1C2A24]/70">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1C2A24]/40" strokeWidth={1.5} />
                  <Input data-testid="confirm-password" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} className="pl-10 rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] h-12" placeholder="Confirm password" required />
                </div>
              </div>
              {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-2xl text-sm" data-testid="reset-error">{error}</div>}
              <Button data-testid="reset-submit" type="submit" disabled={loading} className="w-full h-12 rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-medium">
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
